import React, { useState } from 'react';
import { PenLine, Trash2, UserPlus, Check, Undo2, X } from 'lucide-react';
import { EinweisungTeilnehmer } from '../sige-plan/types';
import { SignaturePad } from '../common/SignaturePad';
import { TeilnehmerErfassung } from './TeilnehmerErfassung';
import {
  LEERZEILEN_MAX,
  braucheTeilnehmerRueckfrage,
  entferneTeilnehmer,
  fuegeTeilnehmerEin,
  klemmeLeerzeilen,
  neuerTeilnehmer,
  setzeTeilnehmerFeld,
  setzeUnterschrift,
  zaehleUnterschriften,
} from './einweisungLogic';

interface Props {
  teilnehmer: EinweisungTeilnehmer[];
  onChange: (liste: EinweisungTeilnehmer[]) => void;
  /** Blanko-Unterschriftszeilen, die im PDF zusaetzlich gedruckt werden. */
  leerzeilen: number;
  onLeerzeilenChange: (anzahl: number) => void;
  isReadOnly?: boolean;
  isMobile?: boolean;
}

/** Ein entfernter Teilnehmer, solange die Ruecknahme angeboten wird. */
interface EntfernterTeilnehmer {
  index: number;
  teilnehmer: EinweisungTeilnehmer;
}

/**
 * Die Unterschriftenliste.
 *
 * Zwei Wege stehen gleichberechtigt nebeneinander, und das ist Absicht: wer
 * das Blatt digital fuehrt, laesst jeden Teilnehmer auf dem Geraet
 * unterschreiben; wer es ausdruckt, traegt die Namen ein und laesst die
 * Blanko-Zeilen fuer Nachzuegler mitdrucken. Beides zusammen kommt in der
 * Praxis vor — auf der Baustelle taucht immer noch jemand auf, dessen Name
 * vorher niemand kannte.
 *
 * ─── Loeschen ist hier teurer als anderswo ────────────────────────────────
 * Eine geleistete Unterschrift bekommt man von jemandem, der die Baustelle
 * verlassen hat, nicht ein zweites Mal. Der Papierkorb steht deshalb mit
 * Abstand am Zeilenende, ein unterschriebener Teilnehmer wird nur nach
 * Rueckfrage entfernt, und zurueckholen laesst sich der letzte in jedem Fall.
 */
export const TeilnehmerListe: React.FC<Props> = ({
  teilnehmer,
  onChange,
  leerzeilen,
  onLeerzeilenChange,
  isReadOnly = false,
  isMobile = false,
}) => {
  // Wer unterschreibt gerade? Das Feld liegt auf dem Handy im Vollbild.
  const [signiertId, setSigniertId] = useState<string | null>(null);
  /** Laeuft gerade der Reihum-Modus (Geraet wird herumgereicht)? */
  const [erfassungLaeuft, setErfassungLaeuft] = useState(false);
  const [entfernt, setEntfernt] = useState<EntfernterTeilnehmer | null>(null);
  const gesperrt = isReadOnly;
  const knopf = isMobile ? 'min-h-[44px] min-w-[44px]' : 'min-h-[36px] min-w-[36px]';

  const aktiver = teilnehmer.find((t) => t.id === signiertId) || null;

  const hinzufuegen = () => onChange([...teilnehmer, neuerTeilnehmer(crypto.randomUUID())]);

  const entfernen = (t: EinweisungTeilnehmer, index: number) => {
    if (braucheTeilnehmerRueckfrage(t)) {
      const ok = window.confirm(
        `${t.name || 'Dieser Teilnehmer'} hat bereits unterschrieben. Die Unterschrift wird mit entfernt. Fortfahren?`,
      );
      if (!ok) return;
    }
    setEntfernt({ index, teilnehmer: t });
    onChange(entferneTeilnehmer(teilnehmer, t.id));
  };

  const zurueckholen = () => {
    if (!entfernt) return;
    onChange(fuegeTeilnehmerEin(teilnehmer, entfernt.index, entfernt.teilnehmer));
    setEntfernt(null);
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-bold text-slate-800">
          Teilnehmer
          <span className="ml-2 text-xs font-medium text-slate-500">
            {teilnehmer.length} erfasst · {zaehleUnterschriften(teilnehmer)} unterschrieben
          </span>
        </h3>
        <div className="flex items-center gap-2">
          {/* Der Reihum-Modus ist der Regelfall vor Ort: ein Griff, dann
              wandert das Geraet durch die Runde. Der Einzeleintrag daneben
              bleibt fuer Nachtraege am Schreibtisch. */}
          <button
            type="button"
            onClick={() => setErfassungLaeuft(true)}
            disabled={gesperrt}
            className={`${knopf} px-4 py-2 flex items-center gap-2 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-xl transition-colors disabled:opacity-50`}
          >
            <PenLine className="w-4 h-4" /> Erfassen
          </button>
          <button
            type="button"
            onClick={hinzufuegen}
            disabled={gesperrt}
            title="Eine leere Zeile anlegen und selbst ausfüllen"
            className={`${knopf} px-4 py-2 flex items-center gap-2 text-sm font-bold text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-xl transition-colors disabled:opacity-50`}
          >
            <UserPlus className="w-4 h-4" /> Einzeln
          </button>
        </div>
      </div>

      {/* Ruecknahme des zuletzt entfernten Teilnehmers — samt Unterschrift. */}
      {entfernt && !gesperrt && (
        <div className="p-2 pl-3 bg-slate-800 rounded-xl flex items-center gap-2">
          <span className="flex-1 min-w-0 text-xs text-slate-200 truncate">
            {entfernt.teilnehmer.name || 'Teilnehmer'} entfernt
            {entfernt.teilnehmer.unterschrift ? ' (mit Unterschrift)' : ''}
          </span>
          <button
            type="button"
            onClick={zurueckholen}
            className="min-h-[44px] px-3 shrink-0 flex items-center gap-1.5 text-xs font-bold text-slate-900 bg-white hover:bg-slate-100 rounded-lg transition-colors"
          >
            <Undo2 className="w-3.5 h-3.5" /> Zurückholen
          </button>
          <button
            type="button"
            onClick={() => setEntfernt(null)}
            title="Hinweis schließen"
            className="min-h-[44px] min-w-[44px] shrink-0 flex items-center justify-center text-slate-400 hover:text-white rounded-lg transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100 overflow-hidden">
        {teilnehmer.length === 0 && (
          <p className="px-4 py-4 text-sm text-slate-400">
            Noch niemand erfasst. Ohne Teilnehmer druckt das Blatt nur die Blanko-Zeilen.
          </p>
        )}

        {teilnehmer.map((t, index) => (
          <div key={t.id} className={`p-2 flex gap-2 ${isMobile ? 'flex-col' : 'items-center'}`}>
            {/* Die Funktion (Polier, Bauleiter …) kommt aus der Reihum-Erfassung
                und steht hinter dem Namen; hier wird sie nur gezeigt. */}
            <div className={`${isMobile ? 'w-full' : 'flex-1'} flex items-center gap-2 min-w-0`}>
              <input
                value={t.name}
                onChange={(e) => onChange(setzeTeilnehmerFeld(teilnehmer, t.id, 'name', e.target.value))}
                disabled={gesperrt}
                placeholder="Name"
                className={`${isMobile ? 'min-h-[44px]' : ''} flex-1 min-w-0 px-3 py-2 text-sm text-slate-800 bg-slate-50 rounded-lg outline-none focus:bg-white focus:ring-1 focus:ring-blue-300 disabled:text-slate-500`}
              />
              {(t.funktion || '').trim() && (
                <span className="shrink-0 text-xs text-slate-500 truncate max-w-[9rem]" title="Funktion">
                  {t.funktion!.trim()}
                </span>
              )}
            </div>
            <input
              value={t.firma || ''}
              onChange={(e) => onChange(setzeTeilnehmerFeld(teilnehmer, t.id, 'firma', e.target.value))}
              disabled={gesperrt}
              placeholder="Firma"
              className={`${isMobile ? 'w-full min-h-[44px]' : 'flex-1'} px-3 py-2 text-sm text-slate-600 bg-slate-50 rounded-lg outline-none focus:bg-white focus:ring-1 focus:ring-blue-300 disabled:text-slate-500`}
            />

            {/* Der Papierkorb sitzt am Ende der Reihe, mit Abstand zum
                Unterschreiben-Knopf: mit Handschuh sind das sonst zwei
                Nachbarn, von denen einer die Unterschrift wegnimmt. */}
            <div className={`flex items-center gap-2 ${isMobile ? 'w-full' : 'shrink-0'}`}>
              {t.unterschrift ? (
                <img
                  src={t.unterschrift}
                  alt={`Unterschrift ${t.name}`}
                  className="h-9 w-24 object-contain bg-white border border-slate-200 rounded"
                />
              ) : (
                <span className="text-[11px] text-slate-400 w-24">ohne Unterschrift</span>
              )}

              <button
                type="button"
                onClick={() => setSigniertId(t.id)}
                disabled={gesperrt}
                title={t.unterschrift ? 'Unterschrift ersetzen' : 'Unterschreiben'}
                className={`${knopf} ${isMobile ? 'px-4 gap-2' : ''} flex items-center justify-center rounded-lg transition-colors disabled:opacity-40 ${
                  t.unterschrift ? 'text-emerald-600 hover:bg-emerald-50 bg-emerald-50/60' : 'text-blue-600 hover:bg-blue-50 bg-blue-50/60'
                }`}
              >
                {t.unterschrift ? <Check className="w-4 h-4" /> : <PenLine className="w-4 h-4" />}
                {isMobile && <span className="text-xs font-bold">{t.unterschrift ? 'Ersetzen' : 'Unterschreiben'}</span>}
              </button>

              <button
                type="button"
                onClick={() => entfernen(t, index)}
                disabled={gesperrt}
                title="Teilnehmer entfernen"
                className={`${knopf} ml-auto flex items-center justify-center text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-40`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Blanko-Zeilen: bewusst ein Textfeld mit Ziffernfilter statt
          type="number" — das Zahlenfeld frisst im Bestand nachweislich
          Zwischenstufen beim Tippen. Geklemmt wird auf das, was die Datenbank
          annimmt (siehe klemmeLeerzeilen): eine 50 haette sonst beim Speichern
          das GANZE Blatt scheitern lassen, Unterschriften eingeschlossen. */}
      <label className="flex flex-wrap items-center gap-3 text-sm text-slate-600">
        <span>Zusätzliche Blanko-Zeilen im PDF</span>
        <input
          value={String(leerzeilen)}
          onChange={(e) => onLeerzeilenChange(klemmeLeerzeilen(e.target.value))}
          disabled={gesperrt}
          inputMode="numeric"
          className={`w-16 px-3 py-2 ${isMobile ? 'min-h-[44px]' : ''} text-sm text-center text-slate-800 bg-slate-50 rounded-lg outline-none focus:bg-white focus:ring-1 focus:ring-blue-300 disabled:text-slate-500`}
        />
        <span className="text-xs text-slate-400">für Nachzügler auf dem Ausdruck (max. {LEERZEILEN_MAX})</span>
      </label>

      {erfassungLaeuft && !gesperrt && (
        <TeilnehmerErfassung
          teilnehmer={teilnehmer}
          onChange={onChange}
          onBeenden={() => setErfassungLaeuft(false)}
        />
      )}

      {aktiver && (
        <SignaturePad
          titel={`Unterschrift — ${aktiver.name || 'Teilnehmer'}`}
          hinweis="Bitte im Feld unterschreiben und übernehmen."
          alsOverlay
          abbrechenLabel="Zurück zur Liste"
          onUebernehmen={(dataUrl) => {
            onChange(setzeUnterschrift(teilnehmer, aktiver.id, dataUrl, new Date().toISOString()));
            // Zurueck zur Liste: der Naechste steht schon daneben.
            setSigniertId(null);
          }}
          onAbbrechen={() => setSigniertId(null)}
        />
      )}
    </div>
  );
};
